import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FlashCardItem,
  generateFlashCards,
  saveFlashCardSet,
} from '../../api/leagues';
import '../../styles/global.css';

const DIFFICULTY_LABELS: Record<number, string> = { 1: 'Easy', 2: 'Medium', 3: 'Hard' };

const CreateFlashCardSet: React.FC = () => {
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();

  // Input fields
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [difficulty, setDifficulty] = useState(2);
  const [skillLevel, setSkillLevel] = useState(50);
  const [timeLimit, setTimeLimit] = useState(10);

  // Preview state
  const [cards, setCards] = useState<FlashCardItem[]>([]);
  const [feedback, setFeedback] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasEdited, setHasEdited] = useState(false);
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);

  const handleGenerate = async () => {
    if (!leagueId || !prompt.trim()) return;
    setIsGenerating(true);
    setError('');

    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');
    try {
      const result = await generateFlashCards(leagueId, prompt, difficulty);
      setCards(result.cards);
      setHasEdited(false);
      setFeedback('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate cards');
    } finally {
      localStorage.removeItem('token');
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!leagueId || !prompt.trim()) return;
    if (hasEdited) {
      if (!window.confirm('Regenerating will replace all cards, including your manual edits. Continue?')) {
        return;
      }
    }
    setIsGenerating(true);
    setError('');

    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');
    try {
      const result = await generateFlashCards(leagueId, prompt, difficulty, feedback || null);
      setCards(result.cards);
      setHasEdited(false);
      setFeedback('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to regenerate cards');
    } finally {
      localStorage.removeItem('token');
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!leagueId || !name.trim() || cards.length !== 20) return;
    setIsSaving(true);
    setError('');

    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');
    try {
      await saveFlashCardSet(leagueId, {
        name: name.trim(),
        difficulty,
        skill_level: skillLevel,
        time_limit_seconds: timeLimit,
        prompt,
        cards,
      });
      navigate(`/manager/leagues/${leagueId}`, {
        state: { success: 'Flash card set created!' },
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save flash card set');
    } finally {
      localStorage.removeItem('token');
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (cards.length > 0 && !window.confirm('Discard all cards and go back?')) return;
    navigate(`/manager/leagues/${leagueId}`);
  };

  const updateCard = (index: number, field: keyof FlashCardItem, value: string | string[]) => {
    const updated = [...cards];
    updated[index] = { ...updated[index], [field]: value };
    setCards(updated);
    setHasEdited(true);
  };

  const updateWrongAnswer = (cardIndex: number, waIndex: number, value: string) => {
    const updated = [...cards];
    const wa = [...updated[cardIndex].wrong_answers];
    wa[waIndex] = value;
    updated[cardIndex] = { ...updated[cardIndex], wrong_answers: wa };
    setCards(updated);
    setHasEdited(true);
  };

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">
          <button
            className="link-btn"
            onClick={() => navigate(`/manager/leagues/${leagueId}`)}
            style={{ marginRight: '10px' }}
          >
            &larr; Back
          </button>
          Create Flash Card Set
        </div>
      </nav>

      <div className="container">
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}

        {/* Input Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Set Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Times Tables 6-8"
                maxLength={50}
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what cards to generate... e.g., 'Multiplication facts for 6, 7, 8 times tables'"
                maxLength={500}
                rows={3}
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                  Difficulty
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(Number(e.target.value))}
                  style={{ padding: '8px' }}
                >
                  <option value={1}>Easy</option>
                  <option value={2}>Medium</option>
                  <option value={3}>Hard</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                  Skill Level
                </label>
                <input
                  type="number"
                  value={skillLevel}
                  onChange={(e) => setSkillLevel(Number(e.target.value) || 50)}
                  min={1}
                  max={999}
                  style={{ padding: '8px', width: '80px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                  Time Limit (s)
                </label>
                <input
                  type="number"
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(Number(e.target.value) || 10)}
                  min={1}
                  max={60}
                  style={{ padding: '8px', width: '80px' }}
                />
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              style={{ alignSelf: 'flex-start' }}
            >
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>

        {/* Preview Section */}
        {cards.length > 0 && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3>Preview ({cards.length} cards)</h3>
            <div style={{ overflowX: 'auto', marginTop: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: '8px 4px', textAlign: 'left', width: '30px' }}>#</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left' }}>Question</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left' }}>Correct Answer</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left' }}>Wrong Answers</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 4px', color: '#666' }}>{i + 1}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {editingCell?.row === i && editingCell?.field === 'question' ? (
                          <input
                            type="text"
                            value={card.question}
                            onChange={(e) => updateCard(i, 'question', e.target.value)}
                            onBlur={() => setEditingCell(null)}
                            autoFocus
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          <span
                            onClick={() => setEditingCell({ row: i, field: 'question' })}
                            style={{ cursor: 'pointer' }}
                            title="Click to edit"
                          >
                            {card.question}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {editingCell?.row === i && editingCell?.field === 'correct' ? (
                          <input
                            type="text"
                            value={card.correct_answer}
                            onChange={(e) => updateCard(i, 'correct_answer', e.target.value)}
                            onBlur={() => setEditingCell(null)}
                            autoFocus
                            style={{ width: '100%', padding: '4px' }}
                          />
                        ) : (
                          <span
                            onClick={() => setEditingCell({ row: i, field: 'correct' })}
                            style={{ cursor: 'pointer', fontWeight: 'bold', color: '#28a745' }}
                            title="Click to edit"
                          >
                            {card.correct_answer}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {card.wrong_answers.map((wa, j) => (
                          <span key={j}>
                            {editingCell?.row === i && editingCell?.field === `wa${j}` ? (
                              <input
                                type="text"
                                value={wa}
                                onChange={(e) => updateWrongAnswer(i, j, e.target.value)}
                                onBlur={() => setEditingCell(null)}
                                autoFocus
                                style={{ width: '80px', padding: '2px', marginRight: '4px' }}
                              />
                            ) : (
                              <span
                                onClick={() => setEditingCell({ row: i, field: `wa${j}` })}
                                style={{ cursor: 'pointer', color: '#dc3545', marginRight: '8px' }}
                                title="Click to edit"
                              >
                                {wa}
                              </span>
                            )}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Feedback + Regenerate */}
            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Feedback (optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Adjust the cards... e.g., 'make them harder', 'focus more on 7s and 8s'"
                maxLength={500}
                rows={2}
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box', resize: 'vertical' }}
              />
              <button
                className="btn"
                onClick={handleRegenerate}
                disabled={isGenerating}
                style={{ marginTop: '8px' }}
              >
                {isGenerating ? 'Generating...' : 'Regenerate'}
              </button>
            </div>

            {/* Save / Discard */}
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving || !name.trim()}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button className="btn" onClick={handleDiscard}>
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateFlashCardSet;
